import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, FolderTree } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', parent_id: 'none' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error loading categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Category name cannot be empty');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        parent_id: formData.parent_id && formData.parent_id !== 'none' ? formData.parent_id : null
      };

      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, payload);
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/categories`, payload);
        toast.success('Category added');
      }

      setDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Error saving category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category and its subcategories?')) return;

    try {
      await axios.delete(`${API}/categories/${id}`);
      toast.success('Category deleted');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Error deleting category');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      parent_id: category.parent_id || 'none'
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({ name: '', parent_id: 'none' });
    setEditingCategory(null);
  };

  const parentCategories = categories.filter(c => !c.parent_id);
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-3xl sm:text-4xl font-black font-heading tracking-tight text-primary">Categories</h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">Manage your income and expense categories</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-md w-full sm:w-auto" data-testid="add-category-button">
              <Plus size={18} className="mr-2" />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md bg-card border-border mx-4">
            <DialogHeader>
              <DialogTitle className="text-primary">{editingCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-foreground">Category Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="category-name-input"
                  placeholder="e.g., Materials, Labor, Sales"
                  className="bg-background border-border text-foreground"
                  required
                />
              </div>
              <div>
                <Label htmlFor="parent" className="text-foreground">Parent Category (Optional)</Label>
                <Select value={formData.parent_id} onValueChange={(value) => setFormData({ ...formData, parent_id: value })}>
                  <SelectTrigger data-testid="category-parent-select" className="bg-background border-border text-foreground">
                    <SelectValue placeholder="Select parent category" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="none" className="text-foreground hover:bg-accent">None (Parent Category)</SelectItem>
                    {parentCategories
                      .filter(c => !editingCategory || c.id !== editingCategory.id)
                      .map(cat => (
                        <SelectItem key={cat.id} value={cat.id} className="text-foreground hover:bg-accent">{cat.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Select a parent to create a subcategory</p>
              </div>
              <Button type="submit" className="w-full" data-testid="category-submit-button">
                {editingCategory ? 'Update' : 'Save'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Categories Display */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        ) : parentCategories.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <FolderTree size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-2">No categories yet</p>
            <p className="text-sm text-muted-foreground">Use the button above to add a new category</p>
          </div>
        ) : (
          parentCategories.map((parent) => {
            const subcategories = getSubcategories(parent.id);
            return (
              <div key={parent.id} className="bg-card border border-border rounded-xl shadow-sm overflow-hidden" data-testid={`category-${parent.id}`}>
                {/* Parent Category */}
                <div className="bg-secondary p-4 flex items-center justify-between border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary text-primary-foreground p-2 rounded-lg">
                      <FolderTree size={20} />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-primary">{parent.name}</h3>
                      <p className="text-xs text-muted-foreground">
                        {subcategories.length} subcategories
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(parent)}
                      data-testid={`edit-category-${parent.id}`}
                      className="hover:bg-accent text-foreground"
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(parent.id)}
                      data-testid={`delete-category-${parent.id}`}
                      className="hover:bg-accent"
                    >
                      <Trash2 size={16} className="text-destructive" />
                    </Button>
                  </div>
                </div>

                {/* Subcategories */}
                {subcategories.length > 0 && (
                  <div className="p-4 bg-card">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {subcategories.map((sub) => (
                        <div
                          key={sub.id}
                          className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border hover:bg-accent transition-colors"
                          data-testid={`subcategory-${sub.id}`}
                        >
                          <span className="text-sm font-medium text-foreground">{sub.name}</span>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(sub)}
                              data-testid={`edit-subcategory-${sub.id}`}
                              className="hover:bg-secondary"
                            >
                              <Pencil size={14} className="text-foreground" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(sub.id)}
                              data-testid={`delete-subcategory-${sub.id}`}
                              className="hover:bg-secondary"
                            >
                              <Trash2 size={14} className="text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}